import { StaticImage } from "gatsby-plugin-image"
import React, { useContext, useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import ContentDiv from "../components/contentDiv"
import Layout from "../components/layout"
import PageImage from "../components/pageImage"
import PageSection from "../components/pageSection"
import UnfoldBoxList, { ListItem } from "../components/unfoldBoxList"
import Tiles from "../components/tiles"
import FirstSection from "../components/firstSection"
import styled from "styled-components"
import Text, { TextType } from "../components/text"
import Anforderungen from "../icons/anforderungen.svg"
import DasBietenWir from "../icons/das-bieten-wir.svg"
import DasErwartenWir from "../icons/das-erwarten-wir.svg"
import Aufgaben from "../icons/aufgaben.svg"
import { laptop } from "../util/mediaQueries"
import JobOffering from "../types/jobOffering"
import { BackendContext } from "../services"
import { Media } from "gatsby-plugin-fresnel"
import SEO from "../components/seo"

const StyledUl = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 10px;
  padding-left: 20px;
  margin-top: 0;
  min-height: 100%;
  height: 100%;
`

const StyledLi = styled.li`
  font-size: 12px;
`

const OffersDiv = styled.div`
  @media ${laptop} {
    margin-right: 0;
    margin-left: 0;
  }
`

const AnforderungenIcon = styled(Anforderungen)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`
const DasBietenWirIcon = styled(DasBietenWir)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`
const DasErwartenWirIcon = styled(DasErwartenWir)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`
const AufgabenIcon = styled(Aufgaben)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`

const QuizContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin: 20px 0;
  flex-wrap: wrap;
  background: white;
  border: 2px solid ${props => props.theme.main};
  border-radius: 15px;
  padding: 25px;
  box-shadow: ${props => props.theme.shadow};
`

const QuizLabel = styled.div`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  text-align: center;
`

const QuizButton = styled.button`
  display: inline-block;
  text-decoration: none;
  color: white;
  background: ${props => props.theme.main};
  padding: 15px 30px;
  border-radius: 10px;
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  text-align: center;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowHovered};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const PopupContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  max-width: 600px;
  min-width: 300px;
  width: 90%;
  box-shadow: ${props => props.theme.shadowHovered};
  text-align: center;
`

const PopupTitle = styled.h3`
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.subHeaderSize};
  margin-bottom: 15px;
`

const PopupButton = styled.button`
  padding: 12px 25px;
  border-radius: 8px;
  font-size: ${props => props.theme.textSize};
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-1px);
  }
`

const CancelButton = styled(PopupButton)`
  background: ${props => props.theme.second};
  color: ${props => props.theme.textColor};
  
  &:hover {
    background: ${props => props.theme.third};
  }
`

const OkButton = styled(PopupButton)`
  background: ${props => props.theme.main};
  color: white;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowHovered};
  }
`

const getBodyUl = (lines: string[]) => (
  <StyledUl>
    {lines.map((line, index) => (
      <StyledLi key={index}>
        <Text type={TextType.Body}>{line}</Text>
      </StyledLi>
    ))}
  </StyledUl>
)

const MwdText = () => <Text type={TextType.Body}>(m/w/d)</Text>

const getJobBox: (
  screenType: string
) => (desc: JobOffering) => ListItem = screenType => desc => {
  const tileList = [
    {
      title: "Aufgaben",
      icon: <AufgabenIcon />,
      body: getBodyUl(desc.aufgaben),
    },
    {
      title: "Anforderungen",
      icon: <AnforderungenIcon />,
      body: getBodyUl(desc.anforderungen),
    },
    {
      title: "Das erwarten wir",
      icon: <DasErwartenWirIcon />,
      body: getBodyUl(desc.dasErwartenWir),
    },
    {
      title: "Das bieten wir",
      icon: <DasBietenWirIcon />,
      body: getBodyUl(desc.dasBietenWir),
    },
  ]
  const maxHeight = `${
    screenType === "lg" || screenType === "xl"
      ? desc.maxHeightLarge
      : screenType === "md"
      ? desc.maxHeightTablet
      : desc.maxHeightPhone
  }px`

  return {
    title: desc.name,
    body: <Tiles tileList={tileList} />,
    icon: [<MwdText />, <MwdText />],
    maxHeight: maxHeight,
  }
}

const QuizForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const QuizStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const QuizInput = styled.input`
  padding: 12px;
  border: 2px solid ${props => props.theme.second};
  border-radius: 8px;
  font-size: ${props => props.theme.textSize};
  color: ${props => props.theme.textColor};
  background: white;
  text-align: right;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.main};
  }
`

const FormButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
  margin-top: 10px;
`

const SubmitButton = styled(OkButton)`
  flex: 1;
  padding: 15px;
`

const BackButton = styled(CancelButton)`
  flex: 1;
  padding: 15px;
`

const StepIndicator = styled.div`
  text-align: center;
  color: ${props => props.theme.textColor};
  font-size: 14px;
  margin-bottom: 10px;
`

const SuccessMessage = styled.div`
  text-align: center;
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  padding: 20px;
`

const RadioOptionContainer = styled.div<{ horizontal?: boolean; wiggle?: boolean }>`
  display: flex;
  flex-direction: ${props => props.horizontal ? "row" : "column"};
  gap: 12px;
  flex-wrap: wrap;
  animation: ${props => props.wiggle ? "wiggle 0.5s ease-in-out" : "none"};
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
  
  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }
`

const RadioOptionLabel = styled.div<{ selected: boolean; horizontal?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  border: 2px solid ${props => props.selected ? props.theme.main : props.theme.second};
  border-radius: 8px;
  background: ${props => props.selected ? props.theme.main + "15" : "white"};
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  flex: ${props => props.horizontal ? "1" : "none"};
  min-width: ${props => props.horizontal ? "120px" : "auto"};
  
  &:hover {
    border-color: ${props => props.theme.main};
    background: ${props => props.theme.main + "10"};
  }
`

const QuestionTitle = styled.div`
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  margin-bottom: 20px;
  text-align: center;
`

const SliderContainer = styled.div<{ wiggle?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 0;
  animation: ${props => props.wiggle ? "wiggle 0.5s ease-in-out" : "none"};
  
  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }
`

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
`

const SliderInput = styled.input<{ value: string }>`
  width: 100%;
  height: 24px;
  border-radius: 4px;
  background: transparent;
  outline: none;
  -webkit-appearance: none;
  margin: 0;
  padding: 0;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => props.theme.main};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    position: relative;
    z-index: 2;
    margin-top: -8px;
  }
  
  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => props.theme.main};
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: ${props => {
      const value = parseInt(props.value || "0");
      const percentage = (value / 5) * 100;
      return `linear-gradient(to right, ${props.theme.main} 0%, ${props.theme.main} ${percentage}%, ${props.theme.second} ${percentage}%, ${props.theme.second} 100%)`;
    }};
    margin-top: 8px;
  }
  
  &::-moz-range-track {
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: ${props => props.theme.second};
  }
  
  &::-moz-range-progress {
    height: 8px;
    border-radius: 4px;
    background: ${props => props.theme.main};
  }
`

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: ${props => props.theme.textSize};
  color: ${props => props.theme.textColor};
`

const SliderLabel = styled.span<{ active: boolean }>`
  font-weight: ${props => props.active ? "bold" : "normal"};
  color: ${props => props.active ? props.theme.main : props.theme.textColor};
`

const SkillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 20px 0;
`

const SkillCategory = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
`

const SkillCategoryName = styled.div`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  font-weight: bold;
  text-align: left;
  flex-shrink: 0;
`

const StarRatingContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StarButton = styled.button<{ filled: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.filled ? props.theme.main : props.theme.second};
  font-size: 28px;
  line-height: 1;
  transition: transform 0.1s ease-in-out;
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`

const ThankYouHeader = styled.h2`
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  margin-bottom: 10px;
  text-align: center;
`

const ThankYouSubtitle = styled.p`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  margin-bottom: 30px;
  text-align: center;
  opacity: 0.8;
`

const ContactFormFields = styled.div<{ wiggle?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 15px;
  animation: ${props => props.wiggle ? "wiggle 0.5s ease-in-out" : "none"};
  
  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }
`

const FormFieldLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  font-weight: bold;
  margin-bottom: 8px;
  
  > span:first-child,
  > *:first-child:not(input) {
    flex-shrink: 0;
    text-align: left;
  }
  
  > input,
  > div {
    flex: 1;
    max-width: 350px;
    min-width: 250px;
  }
`

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid ${props => props.theme.second};
  border-radius: 8px;
  overflow: hidden;
  background: white;
  flex: 1;
  max-width: 350px;
  min-width: 250px;
`

const CountryCode = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px;
  background: ${props => props.theme.second + "20"};
  border-right: 2px solid ${props => props.theme.second};
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  font-weight: bold;
  white-space: nowrap;
  flex-shrink: 0;
`

const PhoneInput = styled.input`
  flex: 1;
  padding: 12px;
  border: none;
  font-size: ${props => props.theme.textSize};
  color: ${props => props.theme.textColor};
  background: white;
  text-align: right;
  outline: none;
  
  &:focus {
    outline: none;
  }
`

const UploadTitle = styled.h3`
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  margin-bottom: 20px;
  text-align: center;
`

const DropZone = styled.div<{ isDragActive: boolean; hasFile: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 15px;
  border: 2px dashed ${props => props.isDragActive ? props.theme.main : props.theme.second};
  border-radius: 8px;
  padding: 40px 20px;
  background: ${props => props.isDragActive ? props.theme.main + "10" : "white"};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  min-height: 150px;
  
  &:hover {
    border-color: ${props => props.theme.main};
    background: ${props => props.theme.main + "10"};
  }
`

const UploadIcon = styled.div`
  font-size: 48px;
  color: ${props => props.theme.main};
`

const UploadText = styled.div`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  text-align: center;
`

const FileName = styled.div`
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.textSize};
  font-weight: bold;
  margin-top: 10px;
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 25px 0;
`

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
  margin-top: 2px;
`

const CheckboxLabel = styled.label`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  cursor: pointer;
  line-height: 1.5;
  flex: 1;
`

const PrivacyLink = styled.a`
  color: ${props => props.theme.main};
  text-decoration: underline;
  
  &:hover {
    text-decoration: none;
  }
`

const FileTypeInfo = styled.div<{ wiggle?: boolean }>`
  padding: 10px 15px;
  background: ${props => props.theme.second + "20"};
  border: 1px solid ${props => props.theme.second};
  border-radius: 6px;
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  text-align: center;
  margin-bottom: 15px;
  animation: ${props => props.wiggle ? "wiggle 0.5s ease-in-out" : "none"};
  
  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }
`

const Karriere = () => {
  const { jobOfferings } = useContext(BackendContext)
  const [showPopup, setShowPopup] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    education: "",
    experience: "",
    datevSkills: "0",
    finanzbuchhaltung: "0",
    einkommensteuer: "0",
    jahresabschluesse: "0",
    lohnbuchhaltung: "0",
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [cvFile, setCvFile] = useState<File | null>(null)
  const [privacyConsent, setPrivacyConsent] = useState(false)
  const [wiggleEducation, setWiggleEducation] = useState(false)
  const [wiggleExperience, setWiggleExperience] = useState(false)
  const [wiggleDatevSkills, setWiggleDatevSkills] = useState(false)
  const [wiggleContactInfo, setWiggleContactInfo] = useState(false)
  const [fullNameValue, setFullNameValue] = useState("")
  const [fileTypeError, setFileTypeError] = useState(false)

  const handleGoClick = () => {
    setShowPopup(true)
  }

  const handleCancel = () => {
    setShowPopup(false)
    setCurrentStep(1)
    setFormData({
      education: "",
      experience: "",
      datevSkills: "0",
      finanzbuchhaltung: "0",
      einkommensteuer: "0",
      jahresabschluesse: "0",
      lohnbuchhaltung: "0",
      firstName: "",
      lastName: "",
      email: "",
      phone: ""
    })
    setIsSubmitted(false)
    setCvFile(null)
    setPrivacyConsent(false)
    setFullNameValue("")
    setFileTypeError(false)
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setCvFile(acceptedFiles[0])
      setFileTypeError(false)
    }
  }, [])

  const onDropRejected = useCallback((rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      setFileTypeError(true)
      setTimeout(() => setFileTypeError(false), 2000)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    multiple: false
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleNext = () => {
    // Validate current step before proceeding
    if (currentStep === 1 && !formData.education) {
      setWiggleEducation(true)
      setTimeout(() => setWiggleEducation(false), 500)
      return
    }
    if (currentStep === 2 && !formData.experience) {
      setWiggleExperience(true)
      setTimeout(() => setWiggleExperience(false), 500)
      return
    }
    if (currentStep === 3 && formData.datevSkills === "0") {
      setWiggleDatevSkills(true)
      setTimeout(() => setWiggleDatevSkills(false), 500)
      return
    }
    if (currentStep === 5) {
      // At least firstName must be filled (name field is required), lastName is optional
      // Email and phone are both required
      if (!formData.firstName || !formData.email || !formData.phone) {
        setWiggleContactInfo(true)
        setTimeout(() => setWiggleContactInfo(false), 500)
        return
      }
    }
    
    if (currentStep < 6) {
      // Sync fullNameValue when moving to step 5
      if (currentStep === 4) {
        const reconstructedName = (formData.firstName || "") + (formData.lastName ? " " + formData.lastName : "");
        if (reconstructedName && !fullNameValue) {
          setFullNameValue(reconstructedName);
        }
      }
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    if (!privacyConsent) {
      alert("Bitte stimmen Sie der Datenschutzerklärung zu, um fortzufahren.")
      return
    }
    
    setIsSubmitting(true)

    // Use FormData to handle file uploads
    // Build FormData manually to ensure bot-field is empty (honeypot must be empty for Netlify)
    const formDataToSend = new FormData()
    formDataToSend.append("form-name", "career-quiz")
    // bot-field must be empty string - this is the honeypot field
    // If it has any value, Netlify will mark the submission as spam
    formDataToSend.append("bot-field", "")
    formDataToSend.append("education", formData.education || "")
    formDataToSend.append("experience", formData.experience || "")
    formDataToSend.append("datevSkills", formData.datevSkills || "0")
    formDataToSend.append("finanzbuchhaltung", formData.finanzbuchhaltung || "0")
    formDataToSend.append("einkommensteuer", formData.einkommensteuer || "0")
    formDataToSend.append("jahresabschluesse", formData.jahresabschluesse || "0")
    formDataToSend.append("lohnbuchhaltung", formData.lohnbuchhaltung || "0")
    formDataToSend.append("firstName", formData.firstName || "")
    formDataToSend.append("lastName", formData.lastName || "")
    formDataToSend.append("email", formData.email || "")
    formDataToSend.append("phone", formData.phone || "")
    
    if (cvFile) {
      formDataToSend.append("cv", cvFile)
    }

    try {
      const response = await fetch("/", {
        method: "POST",
        body: formDataToSend,
      })

      if (response.ok) {
        setIsSubmitted(true)
        setTimeout(() => {
          handleCancel()
        }, 3000)
      } else {
        alert(`Es gab einen Fehler beim Senden (Status: ${response.status}). Bitte versuchen Sie es erneut.`)
      }
    } catch (error) {
      alert("Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut.")
    } finally {
      setIsSubmitting(false)
    }
  }
  return (
    <Layout>
      <PageImage>
        <Media greaterThanOrEqual="md">
          <StaticImage
            src="../images/Titelbild_Karriere_PC.jpeg"
            quality={95}
            formats={["AUTO", "WEBP", "AVIF"]}
            alt="Image"
            objectFit="contain"
            layout="fullWidth"
          />
        </Media>
        <Media at="sm">
          <StaticImage
            src="../images/Titelbild_Karriere_Smartp.jpeg"
            quality={95}
            formats={["AUTO", "WEBP", "AVIF"]}
            alt="Image"
            objectFit="contain"
            layout="fullWidth"
          />
        </Media>
      </PageImage>
      <FirstSection headerText="Unsere aktuellen Stellenangebote">
        Wir sind auf der Suche nach engagierten Mitarbeitern, die mit uns
        gemeinsam unsere Mandanten optimal betreuen und am Erfolg teilhaben
        wollen.
      </FirstSection>
      <QuizContainer>
        <QuizLabel>
          In nur 2 Minuten bei uns bewerben
        </QuizLabel>
        <QuizButton onClick={handleGoClick}>
          Go
        </QuizButton>
      </QuizContainer>
      
      {showPopup && (
        <PopupOverlay onClick={handleCancel}>
          <PopupContent onClick={(e) => e.stopPropagation()}>
            <PopupTitle>Bewerbungsquiz</PopupTitle>
            {isSubmitted ? (
              <SuccessMessage>
                Vielen Dank für Ihre Bewerbung! Wir melden uns bald bei Ihnen.
              </SuccessMessage>
            ) : (
              <QuizForm
                name="career-quiz"
                method="POST"
                encType="multipart/form-data"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="form-name" value="career-quiz" />
                <input type="hidden" name="bot-field" />
                
                {currentStep === 1 && (
                  <QuizStep>
                    <StepIndicator>Schritt 1 von 6</StepIndicator>
                    <QuestionTitle>Welche Ausbildung hast du absolviert?</QuestionTitle>
                    <RadioOptionContainer wiggle={wiggleEducation}>
                      <input
                        type="radio"
                        name="education"
                        value="steuerfachangestellte"
                        checked={formData.education === "steuerfachangestellte"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.education === "steuerfachangestellte"}
                        onClick={() => setFormData(prev => ({ ...prev, education: "steuerfachangestellte" }))}
                      >
                        Steuerfachangestellte (m/w/d)
                      </RadioOptionLabel>
                      <input
                        type="radio"
                        name="education"
                        value="steuerfachwirt"
                        checked={formData.education === "steuerfachwirt"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.education === "steuerfachwirt"}
                        onClick={() => setFormData(prev => ({ ...prev, education: "steuerfachwirt" }))}
                      >
                        Steuerfachwirt (m/w/d)
                      </RadioOptionLabel>
                      <input
                        type="radio"
                        name="education"
                        value="bilanzbuchhalter"
                        checked={formData.education === "bilanzbuchhalter"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.education === "bilanzbuchhalter"}
                        onClick={() => setFormData(prev => ({ ...prev, education: "bilanzbuchhalter" }))}
                      >
                        Bilanzbuchhalter (m/w/d)
                      </RadioOptionLabel>
                      <input
                        type="radio"
                        name="education"
                        value="bwl-studium"
                        checked={formData.education === "bwl-studium"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.education === "bwl-studium"}
                        onClick={() => setFormData(prev => ({ ...prev, education: "bwl-studium" }))}
                      >
                        BWL Studium - Schwerpunkt Steuern (m/w/d)
                      </RadioOptionLabel>
                      <input
                        type="radio"
                        name="education"
                        value="andere"
                        checked={formData.education === "andere"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.education === "andere"}
                        onClick={() => setFormData(prev => ({ ...prev, education: "andere" }))}
                      >
                        Andere
                      </RadioOptionLabel>
                    </RadioOptionContainer>
                    <FormButtons>
                      <CancelButton type="button" onClick={handleCancel}>
                        Abbrechen
                      </CancelButton>
                      <OkButton type="button" onClick={handleNext}>
                        Weiter
                      </OkButton>
                    </FormButtons>
                  </QuizStep>
                )}

                {currentStep === 2 && (
                  <QuizStep>
                    <StepIndicator>Schritt 2 von 6</StepIndicator>
                    <QuestionTitle>Wie viel Berufserfahrung hast du?</QuestionTitle>
                    <RadioOptionContainer horizontal wiggle={wiggleExperience}>
                      <input
                        type="radio"
                        name="experience"
                        value="noch-keine"
                        checked={formData.experience === "noch-keine"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.experience === "noch-keine"}
                        horizontal
                        onClick={() => setFormData(prev => ({ ...prev, experience: "noch-keine" }))}
                      >
                        noch keine
                      </RadioOptionLabel>
                      <input
                        type="radio"
                        name="experience"
                        value="1-2-jahre"
                        checked={formData.experience === "1-2-jahre"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.experience === "1-2-jahre"}
                        horizontal
                        onClick={() => setFormData(prev => ({ ...prev, experience: "1-2-jahre" }))}
                      >
                        1-2 Jahre
                      </RadioOptionLabel>
                      <input
                        type="radio"
                        name="experience"
                        value="ueber-3-jahre"
                        checked={formData.experience === "ueber-3-jahre"}
                        onChange={handleInputChange}
                        required
                        style={{ display: "none" }}
                      />
                      <RadioOptionLabel
                        selected={formData.experience === "ueber-3-jahre"}
                        horizontal
                        onClick={() => setFormData(prev => ({ ...prev, experience: "ueber-3-jahre" }))}
                      >
                        &gt; 3 Jahre
                      </RadioOptionLabel>
                    </RadioOptionContainer>
                    <FormButtons>
                      <BackButton type="button" onClick={handleBack}>
                        Zurück
                      </BackButton>
                      <OkButton type="button" onClick={handleNext}>
                        Weiter zum nächsten Schritt
                      </OkButton>
                    </FormButtons>
                  </QuizStep>
                )}

                {currentStep === 3 && (
                  <QuizStep>
                    <StepIndicator>Schritt 3 von 6</StepIndicator>
                    <QuestionTitle>Wie gut sind deine DATEV-Kenntnisse?</QuestionTitle>
                    <SliderContainer wiggle={wiggleDatevSkills}>
                      <SliderWrapper>
                        <SliderInput
                          type="range"
                          min="0"
                          max="5"
                          step="1"
                          name="datevSkills"
                          value={formData.datevSkills}
                          onChange={handleInputChange}
                        />
                        <SliderLabels>
                          {[0, 1, 2, 3, 4, 5].map((value) => (
                            <SliderLabel key={value} active={parseInt(formData.datevSkills) === value}>
                              {value}
                            </SliderLabel>
                          ))}
                        </SliderLabels>
                      </SliderWrapper>
                    </SliderContainer>
                    <FormButtons>
                      <BackButton type="button" onClick={handleBack}>
                        Zurück
                      </BackButton>
                      <OkButton type="button" onClick={handleNext}>
                        Nächster Schritt
                      </OkButton>
                    </FormButtons>
                  </QuizStep>
                )}

                {currentStep === 4 && (
                  <QuizStep>
                    <StepIndicator>Schritt 4 von 6</StepIndicator>
                    <QuestionTitle>Wie gut schätzt du deine Skills ein?</QuestionTitle>
                    <SkillsContainer>
                      <SkillCategory>
                        <SkillCategoryName>Finanzbuchhaltung</SkillCategoryName>
                        <StarRatingContainer>
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <StarButton
                              key={rating}
                              type="button"
                              filled={parseInt(formData.finanzbuchhaltung) >= rating}
                              onClick={() => setFormData(prev => ({ ...prev, finanzbuchhaltung: rating.toString() }))}
                              aria-label={`${rating} von 5 Sternen`}
                            >
                              ★
                            </StarButton>
                          ))}
                        </StarRatingContainer>
                      </SkillCategory>
                      <SkillCategory>
                        <SkillCategoryName>Einkommensteuer</SkillCategoryName>
                        <StarRatingContainer>
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <StarButton
                              key={rating}
                              type="button"
                              filled={parseInt(formData.einkommensteuer) >= rating}
                              onClick={() => setFormData(prev => ({ ...prev, einkommensteuer: rating.toString() }))}
                              aria-label={`${rating} von 5 Sternen`}
                            >
                              ★
                            </StarButton>
                          ))}
                        </StarRatingContainer>
                      </SkillCategory>
                      <SkillCategory>
                        <SkillCategoryName>Jahresabschlüsse</SkillCategoryName>
                        <StarRatingContainer>
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <StarButton
                              key={rating}
                              type="button"
                              filled={parseInt(formData.jahresabschluesse) >= rating}
                              onClick={() => setFormData(prev => ({ ...prev, jahresabschluesse: rating.toString() }))}
                              aria-label={`${rating} von 5 Sternen`}
                            >
                              ★
                            </StarButton>
                          ))}
                        </StarRatingContainer>
                      </SkillCategory>
                      <SkillCategory>
                        <SkillCategoryName>Lohnbuchhaltung</SkillCategoryName>
                        <StarRatingContainer>
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <StarButton
                              key={rating}
                              type="button"
                              filled={parseInt(formData.lohnbuchhaltung) >= rating}
                              onClick={() => setFormData(prev => ({ ...prev, lohnbuchhaltung: rating.toString() }))}
                              aria-label={`${rating} von 5 Sternen`}
                            >
                              ★
                            </StarButton>
                          ))}
                        </StarRatingContainer>
                      </SkillCategory>
                    </SkillsContainer>
                    <FormButtons>
                      <BackButton type="button" onClick={handleBack}>
                        Zurück
                      </BackButton>
                      <OkButton type="button" onClick={handleNext}>
                        Nächster Schritt
                      </OkButton>
                    </FormButtons>
                  </QuizStep>
                )}

                {currentStep === 5 && (
                  <QuizStep>
                    <StepIndicator>Schritt 5 von 6</StepIndicator>
                    <ThankYouHeader>Vielen Dank für die Beantwortung der Fragen.</ThankYouHeader>
                    <ThankYouSubtitle>Trage deine Kontaktdaten ein, damit wir dich zeitnah kontaktieren können.</ThankYouSubtitle>
                    <ContactFormFields wiggle={wiggleContactInfo}>
                      <FormFieldLabel>
                        <span>Name ✏️</span>
                        <QuizInput
                          type="text"
                          name="name"
                          value={fullNameValue}
                          onChange={(e) => {
                            const fullName = e.target.value;
                            setFullNameValue(fullName);
                            // Split by first space only, preserving trailing spaces
                            const firstSpaceIndex = fullName.indexOf(" ");
                            if (firstSpaceIndex === -1) {
                              // No space found, everything is first name
                              setFormData(prev => ({
                                ...prev,
                                firstName: fullName,
                                lastName: ""
                              }))
                            } else {
                              // Split at first space
                              setFormData(prev => ({
                                ...prev,
                                firstName: fullName.substring(0, firstSpaceIndex),
                                lastName: fullName.substring(firstSpaceIndex + 1)
                              }))
                            }
                          }}
                          required
                          placeholder="Vor- und Nachname"
                        />
                      </FormFieldLabel>
                      <FormFieldLabel>
                        <span>E-Mail ✉️</span>
                        <QuizInput
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="deine@email.de"
                        />
                      </FormFieldLabel>
                      <FormFieldLabel>
                        <span>Telefon 📞</span>
                        <PhoneInputContainer>
                          <CountryCode>
                            🇩🇪 +49
                          </CountryCode>
                          <PhoneInput
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                            placeholder="Telefonnummer"
                          />
                        </PhoneInputContainer>
                      </FormFieldLabel>
                    </ContactFormFields>
                    <FormButtons>
                      <BackButton type="button" onClick={handleBack}>
                        Zurück
                      </BackButton>
                      <OkButton type="button" onClick={handleNext}>
                        Weiter zum letzten Schritt
                      </OkButton>
                    </FormButtons>
                  </QuizStep>
                )}

                {currentStep === 6 && (
                  <QuizStep>
                    <StepIndicator>Schritt 6 von 6</StepIndicator>
                    <UploadTitle>Optional: Wenn du möchtest, lade hier noch deinen Lebenslauf hoch.</UploadTitle>
                    <FileTypeInfo wiggle={fileTypeError}>
                      Erlaubte Dateitypen: PDF
                    </FileTypeInfo>
                    <DropZone {...getRootProps()} isDragActive={isDragActive} hasFile={!!cvFile}>
                      <input {...getInputProps()} />
                      {cvFile ? (
                        <>
                          <UploadIcon>📄</UploadIcon>
                          <FileName>{cvFile.name}</FileName>
                          <UploadText>Klicken oder Datei hierher ziehen, um zu ändern</UploadText>
                        </>
                      ) : (
                        <>
                          <UploadIcon>☁️ ⬆️</UploadIcon>
                          <UploadText>
                            Ziehen Sie eine Datei hierher und legen Sie sie dort ab, oder klicken Sie, um eine Datei auszuwählen
                          </UploadText>
                        </>
                      )}
                    </DropZone>
                    <CheckboxContainer>
                      <Checkbox
                        type="checkbox"
                        id="privacy-consent"
                        checked={privacyConsent}
                        onChange={(e) => setPrivacyConsent(e.target.checked)}
                        required
                      />
                      <CheckboxLabel htmlFor="privacy-consent">
                        Mit dem Absenden des Formulars erkläre ich mich einverstanden mit{" "}
                        <PrivacyLink href="/datenschutz" target="_blank" rel="noopener noreferrer">
                          Datenschutzerklärung
                        </PrivacyLink>
                        ,{" "}
                        <PrivacyLink href="/impressum" target="_blank" rel="noopener noreferrer">
                          Impressum
                        </PrivacyLink>
                        {" *"}
                      </CheckboxLabel>
                    </CheckboxContainer>
                    <FormButtons>
                      <BackButton type="button" onClick={handleBack}>
                        Zurück
                      </BackButton>
                      <SubmitButton type="submit" disabled={isSubmitting || !privacyConsent}>
                        {isSubmitting ? "Wird gesendet..." : "Daten absenden"}
                      </SubmitButton>
                    </FormButtons>
                  </QuizStep>
                )}
              </QuizForm>
            )}
          </PopupContent>
        </PopupOverlay>
      )}
      <PageSection>
        <ContentDiv>
          <OffersDiv>
            <Media greaterThanOrEqual="lg">
              <UnfoldBoxList items={jobOfferings.map(getJobBox("lg"))} />
            </Media>
            <Media at="md">
              <UnfoldBoxList items={jobOfferings.map(getJobBox("md"))} />
            </Media>
            <Media at="sm">
              <UnfoldBoxList items={jobOfferings.map(getJobBox("sm"))} />
            </Media>
          </OffersDiv>
        </ContentDiv>
      </PageSection>
    </Layout>
  )
}

// Gatsby Head API - modern way to manage document head
export const Head = () => {
  const seoData = SEO({ lang: "de", title: "Karriere" })
  
  return (
    <>
      <html lang={seoData.lang} />
      <title>{seoData.titleTemplate ? seoData.titleTemplate.replace('%s', seoData.title) : seoData.title}</title>
      {seoData.meta.map((meta, index) => (
        <meta key={index} name={meta.name} property={meta.property} content={meta.content} />
      ))}
    </>
  )
}

export default Karriere
